import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Select,
  Button,
  Table,
  message,
  Space,
  DatePicker,
  Tag,
  Modal,
  Descriptions,
  Empty,
} from 'antd';
import {
  ReloadOutlined,
  DollarOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../config/apiClient';
import MainLayout from '../layout/layout';
import dayjs from 'dayjs';

const DescuentoManual = () => {
  const [form] = Form.useForm();
  const [alumnos, setAlumnos] = useState([]);
  const [loadingAlumnos, setLoadingAlumnos] = useState(false);
  const [creditos, setCreditos] = useState([]);
  const [loadingCreditos, setLoadingCreditos] = useState(false);
  const [alumnoSeleccionado, setAlumnoSeleccionado] = useState(null);
  const [creditoSeleccionado, setCreditoSeleccionado] = useState(null);
  const [modalConfirmacionVisible, setModalConfirmacionVisible] = useState(false);
  const [descontando, setDescontando] = useState(false);

  // Cargar lista de alumnos
  const loadAlumnos = async () => {
    setLoadingAlumnos(true);
    try {
      const response = await apiClient.get('/alumnos');
      if (response.ok) {
        const data = await response.json();
        // Filtrar solo alumnos activos
        const alumnosActivos = (data.data || data || []).filter(alumno => alumno.alu_activo !== false);
        setAlumnos(alumnosActivos);
      } else {
        message.error('Error al cargar los alumnos');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar los alumnos');
    } finally {
      setLoadingAlumnos(false);
    }
  };

  useEffect(() => {
    loadAlumnos();
  }, []);

  // Cargar créditos disponibles del alumno seleccionado
  const loadCreditos = async (alumnoId) => {
    if (!alumnoId) {
      setCreditos([]);
      setCreditoSeleccionado(null);
      return;
    }

    setLoadingCreditos(true);
    try {
      const response = await apiClient.get(`/descuento-creditos/alumno/${alumnoId}/creditos`);
      if (response.ok) {
        const data = await response.json();
        setCreditos(data || []);
        setCreditoSeleccionado(null);
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al cargar los créditos');
        setCreditos([]);
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar los créditos');
      setCreditos([]);
    } finally {
      setLoadingCreditos(false);
    }
  };

  // Manejar cambio de alumno
  const handleAlumnoChange = (alumnoId) => {
    const alumno = alumnos.find(a => a.alu_id === alumnoId);
    setAlumnoSeleccionado(alumno || null);
    loadCreditos(alumnoId);
  };

  // Manejar selección de crédito
  const handleCreditoSelect = (creditoId) => {
    const credito = creditos.find(c => c.cre_id === creditoId);
    setCreditoSeleccionado(credito || null);
  };

  // Abrir modal de confirmación
  const handleDescontar = () => {
    if (!alumnoSeleccionado || !creditoSeleccionado) {
      message.warning('Por favor selecciona un alumno y un crédito');
      return;
    }
    setModalConfirmacionVisible(true);
  };

  // Confirmar y descontar crédito
  const confirmarDescuento = async () => {
    setDescontando(true);
    try {
      const fechaTurno = form.getFieldValue('fechaTurno');
      
      const dataToSend = {
        alumnoId: alumnoSeleccionado.alu_id,
        creditoId: creditoSeleccionado.cre_id,
        fechaTurno: fechaTurno ? fechaTurno.format('YYYY-MM-DD') : null,
      };

      const response = await apiClient.post('/descuento-creditos/descontar-manual', dataToSend);
      if (response.ok) {
        const resultado = await response.json();
        message.success('Crédito descontado correctamente');
        setModalConfirmacionVisible(false);
        form.resetFields();
        setAlumnoSeleccionado(null);
        setCreditoSeleccionado(null);
        setCreditos([]);
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al descontar el crédito');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al descontar el crédito');
    } finally {
      setDescontando(false);
    }
  };

  // Columnas de la tabla de créditos
  const columnsCreditos = [
    {
      title: 'ID',
      dataIndex: 'cre_id',
      key: 'cre_id',
      width: 80,
    },
    {
      title: 'Plan',
      dataIndex: 'plan_nombre',
      key: 'plan_nombre',
    },
    {
      title: 'Fecha Adquisición',
      dataIndex: 'cre_fecha_adquisicion',
      key: 'cre_fecha_adquisicion',
      render: (fecha) => fecha ? dayjs(fecha).format('DD/MM/YYYY') : '-',
    },
    {
      title: 'Fecha Vencimiento',
      dataIndex: 'cre_fecha_vencimiento',
      key: 'cre_fecha_vencimiento',
      render: (fecha) => {
        const fechaVenc = dayjs(fecha);
        const hoy = dayjs();
        const diasRestantes = fechaVenc.diff(hoy, 'days');
        
        if (diasRestantes < 0) {
          return <Tag color="red">{fechaVenc.format('DD/MM/YYYY')} (Vencido)</Tag>;
        } else if (diasRestantes <= 7) {
          return <Tag color="orange">{fechaVenc.format('DD/MM/YYYY')} ({diasRestantes} días)</Tag>;
        } else {
          return <Tag color="green">{fechaVenc.format('DD/MM/YYYY')} ({diasRestantes} días)</Tag>;
        }
      },
    },
  ];

  return (
    <MainLayout>
      <div style={{ padding: '20px' }}>
        <Card>
          <h2 style={{ margin: 0, marginBottom: '20px' }}>
            Descuento Manual de Créditos
          </h2>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleDescontar}
          >
            <Form.Item
              label="Alumno"
              name="alumnoId"
              rules={[{ required: true, message: 'Por favor selecciona un alumno' }]}
            >
              <Select
                placeholder="Selecciona un alumno"
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                loading={loadingAlumnos}
                onChange={handleAlumnoChange}
                notFoundContent={loadingAlumnos ? 'Cargando...' : 'No hay alumnos disponibles'}
              >
                {alumnos.map(alumno => (
                  <Select.Option
                    key={alumno.alu_id}
                    value={alumno.alu_id}
                    label={`${alumno.alu_nombre || ''} ${alumno.alu_apellido || ''}`.trim()}
                  >
                    {`${alumno.alu_nombre || ''} ${alumno.alu_apellido || ''}`.trim()}
                    {alumno.alu_email ? ` (${alumno.alu_email})` : ''}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            {alumnoSeleccionado && (
              <>
                <Form.Item
                  label="Fecha del Turno (Opcional)"
                  name="fechaTurno"
                  tooltip="Si se especifica una fecha, se registrará como fecha del turno. Si se deja vacío, se usará la fecha actual."
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD/MM/YYYY"
                    placeholder="Selecciona una fecha (opcional)"
                  />
                </Form.Item>

                <div style={{ marginBottom: '20px' }}>
                  <h3>Créditos Disponibles</h3>
                  {loadingCreditos ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      Cargando créditos...
                    </div>
                  ) : creditos.length === 0 ? (
                    <Empty description="El alumno no tiene créditos disponibles" />
                  ) : (
                    <Table
                      columns={columnsCreditos}
                      dataSource={creditos}
                      rowKey="cre_id"
                      pagination={false}
                      rowSelection={{
                        type: 'radio',
                        selectedRowKeys: creditoSeleccionado ? [creditoSeleccionado.cre_id] : [],
                        onChange: (selectedRowKeys) => {
                          if (selectedRowKeys.length > 0) {
                            handleCreditoSelect(selectedRowKeys[0]);
                          } else {
                            setCreditoSeleccionado(null);
                          }
                        },
                      }}
                      onRow={(record) => ({
                        onClick: () => {
                          handleCreditoSelect(record.cre_id);
                          form.setFieldsValue({ creditoId: record.cre_id });
                        },
                        style: { cursor: 'pointer' },
                      })}
                    />
                  )}
                </div>

                <Form.Item>
                  <Space>
                    <Button
                      type="primary"
                      icon={<DollarOutlined />}
                      onClick={handleDescontar}
                      disabled={!creditoSeleccionado || creditos.length === 0}
                      loading={descontando}
                    >
                      Descontar Crédito
                    </Button>
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={() => {
                        if (alumnoSeleccionado) {
                          loadCreditos(alumnoSeleccionado.alu_id);
                        }
                      }}
                    >
                      Actualizar Créditos
                    </Button>
                  </Space>
                </Form.Item>
              </>
            )}
          </Form>
        </Card>

        {/* Modal de Confirmación */}
        <Modal
          title="Confirmar Descuento de Crédito"
          open={modalConfirmacionVisible}
          onOk={confirmarDescuento}
          onCancel={() => setModalConfirmacionVisible(false)}
          okText="Confirmar Descuento"
          cancelText="Cancelar"
          okButtonProps={{ loading: descontando, danger: true }}
        >
          {alumnoSeleccionado && creditoSeleccionado && (
            <Descriptions bordered column={1}>
              <Descriptions.Item label="Alumno">
                {`${alumnoSeleccionado.alu_nombre || ''} ${alumnoSeleccionado.alu_apellido || ''}`.trim()}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {alumnoSeleccionado.alu_email || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Crédito ID">
                {creditoSeleccionado.cre_id}
              </Descriptions.Item>
              <Descriptions.Item label="Plan">
                {creditoSeleccionado.plan_nombre || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Fecha de Vencimiento">
                {dayjs(creditoSeleccionado.cre_fecha_vencimiento).format('DD/MM/YYYY')}
              </Descriptions.Item>
              <Descriptions.Item label="Fecha del Turno">
                {form.getFieldValue('fechaTurno')
                  ? form.getFieldValue('fechaTurno').format('DD/MM/YYYY')
                  : 'Fecha actual'}
              </Descriptions.Item>
            </Descriptions>
          )}
          <div style={{ marginTop: '20px', color: '#ff4d4f' }}>
            <strong>⚠️ Advertencia:</strong> Esta acción no se puede deshacer. El crédito será marcado como utilizado.
          </div>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default DescuentoManual;

